import axios from 'axios'
import { adminApiClient } from '@/services/admin/baseService'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type {
  ApiResponse,
  MetaRefundRequestPayload,
  SolanaOperationEnvelope,
  SolanaOperationList,
  SolanaOperationMode,
  SolanaOperationState,
  SolanaPendingMetaRefundItem,
} from '@/types'

const STORAGE_PREFIX = 'cms:admin:solana-refund-pending:v1'
const API_PREFIX = '/api/v1'
const MAX_PENDING_BYTES = 512 * 1024
const MAX_REQUEST_BODY_BYTES = 64 * 1024
const MAX_LIST_PAGES = 10
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u
const OPERATION_ID_PATTERN = /^svo_[0-9a-f]{64}$/u
const BASE58_PATTERN = /^[1-9A-HJ-NP-Za-km-z]+$/u
const OPERATION_STATES = new Set<SolanaOperationState>([
  'reserved',
  'queued',
  'executing',
  'prepared',
  'broadcast',
  'unknown',
  'completed',
  'rejected',
  'failed',
])
const TERMINAL_STATES = new Set<SolanaOperationState>([
  'completed',
  'rejected',
  'failed',
])
const PUBLIC_ERROR_CODES = new Set([
  'SOLANA_OPERATION_REJECTED',
  'SOLANA_OPERATION_FAILED',
  'SOLANA_OPERATION_RECOVERY_PENDING',
  'SOLANA_OPERATION_PROJECTION_CONFLICT',
])

export interface HydratedSolanaMetaRefunds {
  items: SolanaPendingMetaRefundItem[]
  quarantinedKeys: string[]
}

export class SolanaOperationStorageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SolanaOperationStorageError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const hasExactKeys = (
  value: Record<string, unknown>,
  required: string[],
  optional: string[] = []
): boolean => {
  const allowed = new Set([...required, ...optional])
  return required.every(key => Object.prototype.hasOwnProperty.call(value, key))
    && Object.keys(value).every(key => allowed.has(key))
}

const byteLength = (value: string): number => new TextEncoder().encode(value).byteLength

const requireAdminId = (adminId: string): void => {
  if (!/^[A-Za-z0-9_-]{1,128}$/u.test(adminId)) {
    throw new SolanaOperationStorageError('Admin identity is invalid for operation storage')
  }
}

const requireIdempotencyKey = (value: string): void => {
  if (!UUID_V4_PATTERN.test(value)) {
    throw new SolanaOperationStorageError('Solana operation idempotency key is invalid')
  }
}

const requireResourceId = (value: string): void => {
  if (
    value.length === 0
    || value !== value.trim()
    || value !== value.normalize('NFC')
    || byteLength(value) > 200
    || [...value].some(character => {
      const codePoint = character.codePointAt(0)
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
    })
  ) {
    throw new SolanaOperationStorageError('Solana operation resource ID is invalid')
  }
}

const requireOrderId = (value: unknown): string => {
  if (
    typeof value !== 'string'
    || !/^[A-Za-z0-9_-]{1,256}$/u.test(value)
  ) {
    throw new SolanaOperationStorageError('Solana refund order ID is invalid')
  }
  return value
}

const requireIsoTimestamp = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false
  }
  try {
    return new Date(value).toISOString() === value
  } catch {
    return false
  }
}

const requireRequestBody = (value: unknown): MetaRefundRequestPayload => {
  const normalizedReason = isRecord(value) && typeof value.reason === 'string'
    ? value.reason.trim().normalize('NFC')
    : ''
  if (
    !isRecord(value)
    || !hasExactKeys(value, ['recipients', 'amounts', 'reason'])
    || !Array.isArray(value.recipients)
    || !Array.isArray(value.amounts)
    || normalizedReason !== value.reason
    || [...normalizedReason].length < 3
    || byteLength(normalizedReason) > 500
    || value.recipients.length < 1
    || value.recipients.length > 5
    || value.recipients.length !== value.amounts.length
    || value.recipients.some(item => (
      typeof item !== 'string'
      || item.length < 32
      || item.length > 44
      || !BASE58_PATTERN.test(item)
    ))
    || value.amounts.some(item => (
      typeof item !== 'string'
      || !/^[1-9][0-9]*$/u.test(item)
      || item.length > 20
      || BigInt(item) >= (1n << 64n)
    ))
    || byteLength(JSON.stringify(value)) > MAX_REQUEST_BODY_BYTES
  ) {
    throw new SolanaOperationStorageError('Solana refund request body is invalid')
  }
  return {
    recipients: [...value.recipients] as string[],
    amounts: [...value.amounts] as string[],
    reason: normalizedReason,
  }
}

const getStorage = (): Storage => {
  if (typeof window === 'undefined' || !window.localStorage) {
    throw new SolanaOperationStorageError('Persistent operation storage is unavailable')
  }
  return window.localStorage
}

const getPrefix = (
  adminId: string,
  mode: SolanaOperationMode,
  resourceId: string
): string => {
  requireAdminId(adminId)
  requireResourceId(resourceId)
  return `${STORAGE_PREFIX}:${mode}:${adminId}:${encodeURIComponent(resourceId)}:`
}

const getStorageKey = (
  adminId: string,
  mode: SolanaOperationMode,
  resourceId: string,
  idempotencyKey: string
): string => {
  requireIdempotencyKey(idempotencyKey)
  return `${getPrefix(adminId, mode, resourceId)}${idempotencyKey}`
}

const buildEndpoint = (orderId: string): string =>
  `/admin/orders/${encodeURIComponent(requireOrderId(orderId))}/meta-refund`

const buildStatusPath = (resourceId: string, operationId: string): string =>
  `/admin/solana-operations/refund_order/${encodeURIComponent(resourceId)}/${operationId}`

const buildStatusUrl = (resourceId: string, operationId: string): string =>
  `${API_PREFIX}${buildStatusPath(resourceId, operationId)}`

const readPrefixEntries = (
  storage: Storage,
  prefix: string
): Array<{ key: string; value: string }> => {
  const entries: Array<{ key: string; value: string }> = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key?.startsWith(prefix)) {
      continue
    }
    const value = storage.getItem(key)
    if (value !== null) {
      entries.push({ key, value })
    }
  }
  return entries
}

const writeAndReadback = (
  storage: Storage,
  key: string,
  item: SolanaPendingMetaRefundItem,
  prefix: string
): void => {
  const serialized = JSON.stringify(item)
  const otherBytes = readPrefixEntries(storage, prefix).reduce(
    (total, entry) => entry.key === key
      ? total
      : total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (otherBytes + byteLength(key) + byteLength(serialized) > MAX_PENDING_BYTES) {
    throw new SolanaOperationStorageError('Persistent Solana operation storage is full')
  }
  try {
    storage.setItem(key, serialized)
    if (storage.getItem(key) !== serialized) {
      throw new Error('readback mismatch')
    }
  } catch {
    throw new SolanaOperationStorageError('Unable to persist the Solana operation key')
  }
}

const parsePendingItem = (
  value: unknown,
  expectedKey: string
): SolanaPendingMetaRefundItem => {
  if (
    !isRecord(value)
    || !hasExactKeys(
      value,
      [
        'idempotencyKey',
        'operationType',
        'endpoint',
        'requestBody',
        'orderId',
        'resourceType',
        'resourceId',
        'createdAt',
      ],
      ['operationId', 'statusUrl', 'lastAttemptAt']
    )
    || value.idempotencyKey !== expectedKey
    || value.operationType !== 'solana_platform_meta_refund'
    || value.resourceType !== 'refund_order'
    || typeof value.resourceId !== 'string'
  ) {
    throw new SolanaOperationStorageError('Stored Solana operation item is invalid')
  }
  requireIdempotencyKey(expectedKey)
  requireResourceId(value.resourceId)
  const orderId = requireOrderId(value.orderId)
  if (value.endpoint !== buildEndpoint(orderId)) {
    throw new SolanaOperationStorageError('Stored Solana operation endpoint is invalid')
  }
  const requestBody = requireRequestBody(value.requestBody)
  if (
    !requireIsoTimestamp(value.createdAt)
    || (value.lastAttemptAt !== undefined && !requireIsoTimestamp(value.lastAttemptAt))
  ) {
    throw new SolanaOperationStorageError('Stored Solana operation timestamp is invalid')
  }
  if (
    value.operationId !== undefined
    && (typeof value.operationId !== 'string' || !OPERATION_ID_PATTERN.test(value.operationId))
  ) {
    throw new SolanaOperationStorageError('Stored Solana operation ID is invalid')
  }
  const expectedStatusUrl = value.operationId === undefined
    ? undefined
    : buildStatusUrl(value.resourceId, value.operationId)
  if (
    (value.statusUrl === undefined) !== (expectedStatusUrl === undefined)
    || (value.statusUrl !== undefined && value.statusUrl !== expectedStatusUrl)
  ) {
    throw new SolanaOperationStorageError('Stored Solana operation status URL is invalid')
  }
  return {
    idempotencyKey: expectedKey,
    operationType: 'solana_platform_meta_refund',
    endpoint: buildEndpoint(orderId),
    requestBody,
    orderId,
    resourceType: 'refund_order',
    resourceId: value.resourceId,
    ...(value.operationId === undefined ? {} : { operationId: value.operationId }),
    ...(expectedStatusUrl === undefined ? {} : { statusUrl: expectedStatusUrl }),
    createdAt: value.createdAt,
    ...(value.lastAttemptAt === undefined ? {} : { lastAttemptAt: value.lastAttemptAt }),
  }
}

const createUuidV4 = (): string => {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new SolanaOperationStorageError('Secure operation key generation is unavailable')
  }
  const value = crypto.randomUUID().toLowerCase()
  requireIdempotencyKey(value)
  return value
}

export const hydratePendingSolanaMetaRefunds = (
  adminId: string,
  mode: SolanaOperationMode,
  resourceId: string
): HydratedSolanaMetaRefunds => {
  const storage = getStorage()
  const prefix = getPrefix(adminId, mode, resourceId)
  const entries = readPrefixEntries(storage, prefix)
  const totalBytes = entries.reduce(
    (total, entry) => total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (totalBytes > MAX_PENDING_BYTES) {
    throw new SolanaOperationStorageError('Persistent Solana operation storage is over capacity')
  }
  const items: SolanaPendingMetaRefundItem[] = []
  const quarantinedKeys: string[] = []
  entries.forEach(entry => {
    const suffix = entry.key.slice(prefix.length)
    try {
      items.push(parsePendingItem(JSON.parse(entry.value), suffix))
    } catch {
      quarantinedKeys.push(entry.key)
    }
  })
  return {
    items: items.sort((left, right) => left.createdAt.localeCompare(right.createdAt)),
    quarantinedKeys,
  }
}

const sameRequest = (
  left: MetaRefundRequestPayload,
  right: MetaRefundRequestPayload
): boolean => JSON.stringify(left) === JSON.stringify(right)

export const createOrReusePendingSolanaMetaRefund = (
  adminId: string,
  mode: SolanaOperationMode,
  orderId: string,
  resourceId: string,
  requestBody: MetaRefundRequestPayload
): SolanaPendingMetaRefundItem => {
  requireResourceId(resourceId)
  const normalizedOrderId = requireOrderId(orderId)
  const normalizedBody = requireRequestBody(requestBody)
  const hydrated = hydratePendingSolanaMetaRefunds(adminId, mode, resourceId)
  if (hydrated.quarantinedKeys.length > 0) {
    throw new SolanaOperationStorageError(
      'A stored Solana refund operation is isolated and must be resolved first'
    )
  }
  const existing = hydrated.items.filter(item => item.resourceId === resourceId)
  if (existing.length > 0) {
    if (existing.length !== 1) {
      throw new SolanaOperationStorageError(
        'Multiple Solana refund operations are pending for this order'
      )
    }
    const latest = existing.at(-1)!
    if (
      latest.orderId !== normalizedOrderId
      || !sameRequest(latest.requestBody, normalizedBody)
    ) {
      throw new SolanaOperationStorageError(
        'Another Solana refund operation is pending for this order'
      )
    }
    return latest
  }
  const idempotencyKey = createUuidV4()
  const item: SolanaPendingMetaRefundItem = {
    idempotencyKey,
    operationType: 'solana_platform_meta_refund',
    endpoint: buildEndpoint(normalizedOrderId),
    requestBody: normalizedBody,
    orderId: normalizedOrderId,
    resourceType: 'refund_order',
    resourceId,
    createdAt: new Date().toISOString(),
  }
  const storage = getStorage()
  writeAndReadback(
    storage,
    getStorageKey(adminId, mode, resourceId, idempotencyKey),
    item,
    getPrefix(adminId, mode, resourceId)
  )
  return item
}

const requirePublicError = (
  value: unknown,
  state: SolanaOperationState
): SolanaOperationEnvelope['error'] => {
  if (
    !isRecord(value)
    || !hasExactKeys(value, ['code', 'message'])
    || typeof value.code !== 'string'
    || !PUBLIC_ERROR_CODES.has(value.code)
    || typeof value.message !== 'string'
  ) {
    throw new Error('Solana operation error is invalid')
  }
  const expectedCodes: Partial<Record<SolanaOperationState, string[]>> = {
    rejected: ['SOLANA_OPERATION_REJECTED'],
    failed: ['SOLANA_OPERATION_FAILED'],
    unknown: [
      'SOLANA_OPERATION_RECOVERY_PENDING',
      'SOLANA_OPERATION_PROJECTION_CONFLICT',
    ],
  }
  if (!expectedCodes[state]?.includes(value.code)) {
    throw new Error('Solana operation error does not match its state')
  }
  return value as unknown as SolanaOperationEnvelope['error']
}

const requireTerminalResult = (value: unknown, signature: string | undefined) => {
  if (
    !isRecord(value)
    || !hasExactKeys(value, ['outcome', 'signature', 'slot', 'evidence'])
    || value.outcome !== 'confirmed'
    || typeof value.signature !== 'string'
    || !BASE58_PATTERN.test(value.signature)
    || value.signature !== signature
    || !Number.isSafeInteger(value.slot)
    || (value.slot as number) < 0
    || !isRecord(value.evidence)
    || !hasExactKeys(
      value.evidence,
      ['transactionObservedAtMs', 'finalizedSlot', 'errorCode']
    )
    || !Number.isSafeInteger(value.evidence.transactionObservedAtMs)
    || (value.evidence.transactionObservedAtMs as number) < 0
    || !Number.isSafeInteger(value.evidence.finalizedSlot)
    || value.evidence.finalizedSlot !== value.slot
    || value.evidence.errorCode !== null
  ) {
    throw new Error('Solana operation result is invalid')
  }
  return value as unknown as NonNullable<SolanaOperationEnvelope['result']>
}

const parseEnvelope = (
  value: unknown,
  resourceId: string,
  mode: SolanaOperationMode
): SolanaOperationEnvelope => {
  if (
    !isRecord(value)
    || !hasExactKeys(
      value,
      [
        'schemaVersion',
        'operationId',
        'operationType',
        'mode',
        'resourceType',
        'resourceId',
        'sequence',
        'state',
        'statusUrl',
        'createdAt',
        'updatedAt',
      ],
      ['signature', 'result', 'error', 'terminalAt']
    )
    || value.schemaVersion !== 'v1'
    || typeof value.operationId !== 'string'
    || !OPERATION_ID_PATTERN.test(value.operationId)
    || value.operationType !== 'solana_platform_meta_refund'
    || value.mode !== mode
    || value.resourceType !== 'refund_order'
    || value.resourceId !== resourceId
    || !Number.isSafeInteger(value.sequence)
    || (value.sequence as number) < 1
    || typeof value.state !== 'string'
    || !OPERATION_STATES.has(value.state as SolanaOperationState)
    || value.statusUrl !== buildStatusUrl(resourceId, value.operationId)
    || !requireIsoTimestamp(value.createdAt)
    || !requireIsoTimestamp(value.updatedAt)
  ) {
    throw new Error('Solana operation response is invalid')
  }
  const state = value.state as SolanaOperationState
  const terminal = TERMINAL_STATES.has(state)
  if (
    (value.terminalAt !== undefined) !== terminal
    || (value.terminalAt !== undefined && !requireIsoTimestamp(value.terminalAt))
  ) {
    throw new Error('Solana operation terminal timestamp is invalid')
  }
  const signature = value.signature === undefined
    ? undefined
    : typeof value.signature === 'string'
      && value.signature.length >= 64
      && value.signature.length <= 128
      && BASE58_PATTERN.test(value.signature)
      ? value.signature
      : null
  if (signature === null) {
    throw new Error('Solana operation signature is invalid')
  }
  if (
    (state === 'completed') !== (value.result !== undefined)
    || (['unknown', 'rejected', 'failed'].includes(state)) !== (value.error !== undefined)
  ) {
    throw new Error('Solana operation state payload is invalid')
  }
  const result = state === 'completed'
    ? requireTerminalResult(value.result, signature)
    : undefined
  const error = value.error === undefined ? undefined : requirePublicError(value.error, state)
  return {
    schemaVersion: 'v1',
    operationId: value.operationId,
    operationType: 'solana_platform_meta_refund',
    mode,
    resourceType: 'refund_order',
    resourceId,
    sequence: value.sequence as number,
    state,
    statusUrl: buildStatusUrl(resourceId, value.operationId),
    ...(signature === undefined ? {} : { signature }),
    ...(result === undefined ? {} : { result }),
    ...(error === undefined ? {} : { error }),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    ...(value.terminalAt === undefined ? {} : { terminalAt: value.terminalAt }),
  }
}

const parseSuccessEnvelope = (
  value: unknown,
  resourceId: string,
  mode: SolanaOperationMode
): SolanaOperationEnvelope => {
  if (
    !isRecord(value)
    || !hasExactKeys(value, ['success', 'data'])
    || value.success !== true
  ) {
    throw new Error('Solana operation response is missing its operation')
  }
  return parseEnvelope(value.data, resourceId, mode)
}

const parseTerminalErrorEnvelope = (
  error: unknown,
  resourceId: string,
  mode: SolanaOperationMode
): SolanaOperationEnvelope | null => {
  if (!axios.isAxiosError(error) || ![409, 422].includes(error.response?.status ?? 0)) {
    return null
  }
  try {
    const body = error.response?.data
    if (
      !isRecord(body)
      || !hasExactKeys(body, ['success', 'error', 'data'])
      || body.success !== false
      || !isRecord(body.error)
      || !hasExactKeys(body.error, ['code', 'message'])
    ) {
      return null
    }
    const envelope = parseEnvelope(body.data, resourceId, mode)
    const expectedState = error.response?.status === 409 ? 'rejected' : 'failed'
    if (
      envelope.state !== expectedState
      || !envelope.error
      || body.error.code !== envelope.error.code
      || body.error.message !== envelope.error.message
    ) {
      return null
    }
    return envelope
  } catch {
    return null
  }
}

const storeEnvelope = (
  adminId: string,
  mode: SolanaOperationMode,
  item: SolanaPendingMetaRefundItem,
  envelope: SolanaOperationEnvelope
): void => {
  const storage = getStorage()
  const key = getStorageKey(adminId, mode, item.resourceId, item.idempotencyKey)
  if (TERMINAL_STATES.has(envelope.state)) {
    storage.removeItem(key)
    return
  }
  writeAndReadback(
    storage,
    key,
    { ...item, operationId: envelope.operationId, statusUrl: envelope.statusUrl },
    getPrefix(adminId, mode, item.resourceId)
  )
}

export const submitPendingSolanaMetaRefund = async (
  adminId: string,
  mode: SolanaOperationMode,
  input: SolanaPendingMetaRefundItem
): Promise<SolanaOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  const attempted: SolanaPendingMetaRefundItem = {
    ...item,
    lastAttemptAt: new Date().toISOString(),
  }
  writeAndReadback(
    getStorage(),
    getStorageKey(adminId, mode, item.resourceId, item.idempotencyKey),
    attempted,
    getPrefix(adminId, mode, item.resourceId)
  )
  const modeConfig = buildModeAwareConfig(mode)
  try {
    const response = await adminApiClient.post<ApiResponse<SolanaOperationEnvelope>>(
      attempted.endpoint,
      attempted.requestBody,
      {
        ...modeConfig,
        headers: {
          ...(modeConfig?.headers ?? {}),
          'Idempotency-Key': attempted.idempotencyKey,
        },
      }
    )
    const envelope = parseSuccessEnvelope(response.data, attempted.resourceId, mode)
    const expectedStatus = envelope.state === 'completed' ? 200 : 202
    if (response.status !== expectedStatus) {
      throw new Error('Solana operation HTTP status does not match its state')
    }
    storeEnvelope(adminId, mode, attempted, envelope)
    return envelope
  } catch (error) {
    const envelope = parseTerminalErrorEnvelope(error, attempted.resourceId, mode)
    if (envelope) {
      storeEnvelope(adminId, mode, attempted, envelope)
      return envelope
    }
    throw error
  }
}

const statusRequestPath = (statusUrl: string): string => {
  if (!statusUrl.startsWith(`${API_PREFIX}/admin/solana-operations/`)) {
    throw new SolanaOperationStorageError('Stored Solana operation status URL is invalid')
  }
  return statusUrl.slice(API_PREFIX.length)
}

export const getSolanaOperationStatus = async (
  item: SolanaPendingMetaRefundItem,
  mode: SolanaOperationMode
): Promise<SolanaOperationEnvelope> => {
  const parsed = parsePendingItem(item, item.idempotencyKey)
  if (!parsed.statusUrl) {
    throw new Error('Solana operation status is unavailable before reservation')
  }
  const response = await adminApiClient.get<ApiResponse<SolanaOperationEnvelope>>(
    statusRequestPath(parsed.statusUrl),
    buildModeAwareConfig(mode)
  )
  return parseSuccessEnvelope(response.data, parsed.resourceId, mode)
}

const isExactOperationNotFound = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) {
    return false
  }
  const body = error.response.data
  return isRecord(body)
    && hasExactKeys(body, ['success', 'error'])
    && body.success === false
    && isRecord(body.error)
    && hasExactKeys(body.error, ['code', 'message'])
    && body.error.code === 'SOLANA_OPERATION_NOT_FOUND'
    && typeof body.error.message === 'string'
}

export const lookupSolanaOperation = async (
  item: SolanaPendingMetaRefundItem,
  mode: SolanaOperationMode
): Promise<SolanaOperationEnvelope> => {
  const parsed = parsePendingItem(item, item.idempotencyKey)
  const modeConfig = buildModeAwareConfig(mode)
  const response = await adminApiClient.get<ApiResponse<SolanaOperationEnvelope>>(
    `/admin/solana-operations/refund_order/${
      encodeURIComponent(parsed.resourceId)
    }/by-idempotency-key`,
    {
      ...modeConfig,
      headers: {
        ...(modeConfig?.headers ?? {}),
        'Idempotency-Key': parsed.idempotencyKey,
      },
    }
  )
  return parseSuccessEnvelope(response.data, parsed.resourceId, mode)
}

export const reconcilePendingSolanaMetaRefund = async (
  adminId: string,
  mode: SolanaOperationMode,
  input: SolanaPendingMetaRefundItem
): Promise<SolanaOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  let envelope: SolanaOperationEnvelope
  if (item.statusUrl) {
    envelope = await getSolanaOperationStatus(item, mode)
  } else {
    try {
      envelope = await lookupSolanaOperation(item, mode)
    } catch (error) {
      if (!isExactOperationNotFound(error)) {
        throw error
      }
      if (!item.lastAttemptAt) {
        throw new Error('Unsent Solana operation draft has no server operation')
      }
      return submitPendingSolanaMetaRefund(adminId, mode, item)
    }
  }
  storeEnvelope(adminId, mode, item, envelope)
  return envelope
}

const listSolanaOperationPage = async (
  resourceId: string,
  mode: SolanaOperationMode,
  afterSequence: number
): Promise<SolanaOperationList> => {
  requireResourceId(resourceId)
  const response = await adminApiClient.get<ApiResponse<SolanaOperationList>>(
    `/admin/solana-operations/refund_order/${encodeURIComponent(resourceId)}`,
    {
      ...buildModeAwareConfig(mode),
      params: { afterSequence, limit: 100 },
    }
  )
  const body = response.data
  const data = body?.data
  if (
    !isRecord(body)
    || !hasExactKeys(body, ['success', 'data'])
    || body.success !== true
    || !isRecord(data)
    || !hasExactKeys(data, [
      'schemaVersion',
      'mode',
      'resourceType',
      'resourceId',
      'items',
      'nextAfterSequence',
    ])
    || data.schemaVersion !== 'v1'
    || data.mode !== mode
    || data.resourceType !== 'refund_order'
    || data.resourceId !== resourceId
    || !Array.isArray(data.items)
  ) {
    throw new Error('Solana operation list response is invalid')
  }
  const items = data.items.map(item => parseEnvelope(item, resourceId, mode))
  items.forEach((item, index) => {
    const previous = index === 0 ? afterSequence : items[index - 1]!.sequence
    if (item.sequence <= previous) {
      throw new Error('Solana operation list sequence is invalid')
    }
  })
  const expectedCursor = items.at(-1)?.sequence ?? null
  if (data.nextAfterSequence !== expectedCursor) {
    throw new Error('Solana operation list cursor is invalid')
  }
  return {
    schemaVersion: 'v1',
    mode,
    resourceType: 'refund_order',
    resourceId,
    items,
    nextAfterSequence: expectedCursor,
  }
}

export const listNonterminalSolanaMetaRefunds = async (
  resourceId: string,
  mode: SolanaOperationMode
): Promise<SolanaOperationEnvelope[]> => {
  const operations: SolanaOperationEnvelope[] = []
  let afterSequence = 0
  for (let page = 0; page < MAX_LIST_PAGES; page += 1) {
    const result = await listSolanaOperationPage(resourceId, mode, afterSequence)
    operations.push(...result.items)
    if (result.nextAfterSequence === null) {
      return operations.filter(item => !TERMINAL_STATES.has(item.state))
    }
    afterSequence = result.nextAfterSequence
  }
  throw new Error('Solana operation list exceeds the safe inspection limit')
}

export const solanaOperationService = {
  createOrReusePending: createOrReusePendingSolanaMetaRefund,
  hydratePending: hydratePendingSolanaMetaRefunds,
  submitPending: submitPendingSolanaMetaRefund,
  reconcilePending: reconcilePendingSolanaMetaRefund,
  listNonterminal: listNonterminalSolanaMetaRefunds,
}
